import { List, ListItem, SelectionItemId } from "@fluentui/react-components";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { ReactNode, useMemo } from "react";

export const DualListBoxSelectList = <T,>({
  selected,
  setSelected,
  initialItemKeysSet,
  items,
  getKey,
  renderItem,
  sort,
  listStyle,
  type,
  itemHeight,
  refList: refListProp,
}: {
  selected: SelectionItemId[];
  setSelected: (selectedItems: SelectionItemId[]) => void;
  initialItemKeysSet: Set<string>;
  items: T[];
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  sort: (a: T, b: T) => number;
  listStyle: React.CSSProperties;
  type: "delete" | "add";
  itemHeight: number;
  refList?: React.RefObject<HTMLDivElement | HTMLUListElement>;
}) => {
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const sortedItems = useMemo(() => [...items].sort(sort), [items, sort]);
  const renderItems = useMemo(
    () => [
      ...[...sortedItems]
        .filter((item) => !initialItemKeysSet.has(getKey(item)))
        .map((item) => ({ item, isDiff: true })),
      ...[...sortedItems]
        .filter((item) => initialItemKeysSet.has(getKey(item)))
        .map((item) => ({ item, isDiff: false })),
    ],
    [initialItemKeysSet, sortedItems]
  );
  const refListLocal = React.useRef<HTMLDivElement | HTMLUListElement>(null);
  const refList = refListProp || refListLocal;
  const rowVirtualizer = useVirtualizer({
    count: renderItems.length,
    getScrollElement: () => refList.current,
    estimateSize: () => itemHeight,
  });
  return (
    <List
      ref={refList}
      selectedItems={selected}
      onSelectionChange={(_, { selectedItems }) => {
        setSelected(selectedItems);
      }}
      style={listStyle}
      selectionMode="multiselect"
    >
      <div
        style={{
          height: rowVirtualizer.getTotalSize(),
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const { item, isDiff } = renderItems[virtualRow.index];
          const key = getKey(item);
          const isSelected = selectedSet.has(key);
          return (
            <div
              style={{
                position: "absolute",
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
              key={key}
            >
              <div
                style={
                  isDiff
                    ? { backgroundColor: type === "add" ? "#cec" : "#ecc" }
                    : {}
                }
              >
                <ListItem
                  style={{
                    backgroundColor: isSelected
                      ? "rgba(0, 0, 0, 0.1)"
                      : "transparent",
                  }}
                  checkmark={null}
                  key={key}
                  value={key}
                >
                  {renderItem(item)}
                </ListItem>
              </div>
              <div style={{ paddingBottom: 1 }} />
            </div>
          );
        })}
      </div>
    </List>
  );
};
